import { PieChart } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Form } from '@unistocks/components/form/form';
import {
  applyRefinements,
  conditionalRequired,
  createNumberSchema
} from '@unistocks/components/form/validators';
import { Sheet, SheetProps } from '@unistocks/components/sheet/sheet';
import { t } from '@unistocks/localization';
import { useProfileUpdateSettingsMutation } from '@unistocks/proxy/axios-client/ProfileQuery';
import { useSettingValue } from '@unistocks/shared/hooks/use-setting-value';
import { UnistocksSettings } from '@unistocks/shared/settings';
import { isNumber } from '@unistocks/shared/utils/util';
import React from 'react';
import { SubmitHandler, useFormContext, useWatch } from 'react-hook-form';
import { CheckedState } from 'tamagui';
import { z } from 'zod';

type PortfolioSettingsFormValues = {
  calendarMonth: boolean;
  transactionBreakpoint: number | null;
};

const schemaFactory = () =>
  applyRefinements(
    z.object({
      calendarMonth: z.boolean(),
      transactionBreakpoint: createNumberSchema()
        .refine(
          value => value === null || value >= 1,
          t('::ValidationError:Min', {
            0: t('::Settings:TransactionBreakpoint'),
            1: 1
          })
        )
        .refine(
          value => value === null || value <= 31,
          t('::ValidationError:Max', {
            0: t('::Settings:TransactionBreakpoint'),
            1: 31
          })
        )
        .nullish()
    }),
    conditionalRequired((calendarMonth: boolean) => !calendarMonth, 'transactionBreakpoint', {
      fieldName: t('::Settings:TransactionBreakpoint'),
      dependsOn: ['calendarMonth']
    })
  );

export const PortfolioSettings: React.FC<Omit<SheetProps, 'children'>> = props => {
  const queryClient = useQueryClient();
  const schema = schemaFactory();

  const transactionBreakpointSetting = useSettingValue(UnistocksSettings.TransactionBreakpoint);
  const transactionBreakpoint = transactionBreakpointSetting
    ? Number.parseInt(transactionBreakpointSetting)
    : null;

  const initialValues: PortfolioSettingsFormValues = {
    calendarMonth: !isNumber(transactionBreakpoint),
    transactionBreakpoint: transactionBreakpoint || null
  };

  const { mutateAsync } = useProfileUpdateSettingsMutation({
    onSuccess: () => {
      // This affects all portfolios, and dashboard net savings, so just invalidate everything
      queryClient.invalidateQueries();
      props.onOpenChange?.(false);
    }
  });

  const onSubmit: SubmitHandler<PortfolioSettingsFormValues> = async ({
    calendarMonth,
    transactionBreakpoint
  }) =>
    mutateAsync({
      settings: {
        [UnistocksSettings.TransactionBreakpoint]: calendarMonth
          ? (null as any)
          : transactionBreakpoint!.toString()
      }
    });

  return (
    <Sheet
      {...props}
      headerTitle={t('::Settings:Portfolio')}
      headerIcon={<PieChart size={'$2'} color={'$blue11'} />}
      size="small"
      scrollMode="view"
      enableDynamicSizing
    >
      <Form schema={schema} initialValues={initialValues} onSubmit={onSubmit}>
        <PortfolioSettingsFormContent />
      </Form>
    </Sheet>
  );
};

const PortfolioSettingsFormContent: React.FC = () => {
  const { setValue } = useFormContext<PortfolioSettingsFormValues>();
  const calendarMonth = useWatch<PortfolioSettingsFormValues, 'calendarMonth'>({
    name: 'calendarMonth'
  });

  const onCheckedChange = (checked: CheckedState) => {
    if (checked === true) {
      setValue('transactionBreakpoint', null);
    }
  };

  return (
    <Form.Section
      label={t('::Settings:TransactionBreakpoint')}
      helpText={`${t('::Settings:TransactionBreakpoint:HelpText1')}\n\n${t(
        '::Settings:TransactionBreakpoint:HelpText2'
      )}`}
    >
      <Form.Checkbox
        mb={'$3'}
        name="calendarMonth"
        label={t('::Settings:TransactionBreakpoint:CalendarMonth')}
        onCheckedChange={onCheckedChange}
      />
      <Form.Input
        name="transactionBreakpoint"
        keyboardType="numeric"
        label={t('::Settings:TransactionBreakpoint')}
        placeholder={calendarMonth ? '' : t('::Settings:TransactionBreakpoint:Placeholder')}
        isDisabled={calendarMonth}
        hidden={calendarMonth}
      />
    </Form.Section>
  );
};

PortfolioSettingsFormContent.displayName = 'PortfolioSettingsFormContent';
